import React, { createContext, useContext, ReactNode, useEffect, useState, useCallback } from 'react';
import { useCancamSocket } from '../../hooks/cancam/useCancamSocket';
import { useActivePatrolData } from '../activePatrol/ActivePatrolDataProvider';
import { useScheduleContext } from '../ScheduleProvider';
import {
    DeviceReplacementRequest,
    DeviceState,
    PairedDevice,
    UploadProgressPayload,
} from 'api/cancam/types';

interface CancamContextType {
    // Connection
    isConnected: boolean;

    // Patrol-based device (replaces isPaired/pairedDevice)
    connectedDevice: PairedDevice | null;
    hasDeviceForPatrol: boolean;

    // Pairing code generation
    patrolPairingCode: string | null;
    patrolCodeExpiry: number | null;
    generatePatrolCode: () => Promise<void>;  // Uses current patrol context

    // Device replacement
    pendingReplacement: DeviceReplacementRequest | null;
    confirmDeviceReplacement: (approve: boolean) => void;

    // Device state & controls
    deviceState: DeviceState | null;
    uploadProgress: UploadProgressPayload | null;
    startRecording: () => void;
    stopRecording: () => void;
    forceUpload: () => void;

    // Drawer
    isDrawerOpen: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;

    // Error handling
    error: string | null;
    clearError: () => void;
}

const CancamContext = createContext<CancamContextType | undefined>(undefined);

export const CancamProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    // Get patrol-based socket hook
    const {
        isConnected,
        error: socketError,
        connectedDevice,
        patrolPairingCode,
        patrolCodeExpiry,
        generatePatrolCode: socketGeneratePatrolCode,
        watchPatrol,
        unwatchPatrol,
        pendingReplacement,
        confirmDeviceReplacement,
        deviceState,
        uploadProgress,
        startRecording: socketStartRecording,
        stopRecording: socketStopRecording,
        forceUpload: socketForceUpload,
    } = useCancamSocket();

    // Get active patrol context for commentListId
    const { activePatrolDataRef, fetchAndSetPatrolData } = useActivePatrolData();

    // Get schedule and currently viewed patrol
    const { schedule, currentViewedPatrol } = useScheduleContext();

    // Find the currently viewed schedule item (patrol or incident)
    const findActiveScheduleItem = useCallback((): { item: typeof currentViewedPatrol; error: string | null } => {
        console.log('[CANcam] findActiveScheduleItem called', {
            currentViewedPatrol,
            scheduleLength: schedule.length,
            scheduleTypes: schedule.map(s => ({ id: s.schedule_assignment_id, type: s.type })),
        });

        // Use the item currently being viewed in the swiper
        // Note: type can be 'Patrol', 'Incident', or empty string (treat empty as Patrol)
        if (currentViewedPatrol && currentViewedPatrol.schedule_assignment_id) {
            console.log('[CANcam] Found viewed item:', currentViewedPatrol.schedule_assignment_id, currentViewedPatrol.type || '(no type)');
            return { item: currentViewedPatrol, error: null };
        }
        // Fallback: find any started item
        const startedItem = schedule.find(item => item.started);
        if (startedItem) {
            console.log('[CANcam] Found started item:', startedItem.schedule_assignment_id, startedItem.type || '(no type)');
            return { item: startedItem, error: null };
        }
        console.log('[CANcam] No item found');
        return { item: null, error: 'No patrol or incident selected. Navigate to one first.' };
    }, [schedule, currentViewedPatrol]);

    // Auto-load active patrol data if not already loaded
    const ensurePatrolDataLoaded = useCallback(async (): Promise<boolean> => {
        // If patrol data is already loaded, we're good
        if (activePatrolDataRef.current?.patrolInfo?.comment_list_id) {
            return true;
        }

        // Find the active schedule item (patrol or incident)
        const { item: activeItem, error: itemError } = findActiveScheduleItem();
        if (!activeItem) {
            setLocalError(itemError || 'No patrol or incident selected. Navigate to one first.');
            return false;
        }

        // Load the patrol/incident data (comment_list_id comes from API response, not schedule item)
        try {
            await fetchAndSetPatrolData(activeItem, false);
            // Verify the data was loaded with a comment_list_id
            if (!activePatrolDataRef.current?.patrolInfo?.comment_list_id) {
                setLocalError('Patrol data incomplete. Please try again.');
                return false;
            }
            return true;
        } catch (err) {
            console.error('[CANcam] Failed to load patrol data:', err);
            setLocalError('Failed to load patrol data. Please open the patrol manually.');
            return false;
        }
    }, [activePatrolDataRef, findActiveScheduleItem, fetchAndSetPatrolData]);

    // Auto-watch current patrol when it changes
    useEffect(() => {
        if (currentViewedPatrol?.comment_list_id) {
            watchPatrol(currentViewedPatrol.comment_list_id);
        }
        return () => {
            unwatchPatrol();
        };
    }, [currentViewedPatrol?.comment_list_id, watchPatrol, unwatchPatrol]);

    // Generate patrol code using current patrol context
    const generatePatrolCode = useCallback(async (): Promise<void> => {
        // Auto-load patrol data if needed
        const loaded = await ensurePatrolDataLoaded();
        if (!loaded) {
            return; // Error already set by ensurePatrolDataLoaded
        }

        const patrolData = activePatrolDataRef.current;
        if (!patrolData?.patrolInfo?.comment_list_id) {
            console.error('[CANcam] No active patrol found for code generation');
            setLocalError('No patrol selected. Navigate to a patrol first.');
            return;
        }

        socketGeneratePatrolCode(
            patrolData.patrolInfo.comment_list_id,
            patrolData.patrolInfo.schedule_assignment_id
        );
    }, [socketGeneratePatrolCode, activePatrolDataRef, ensurePatrolDataLoaded]);

    // Start recording with active patrol's commentListId
    const startRecording = useCallback(async (): Promise<void> => {
        // Auto-load patrol data if needed
        const loaded = await ensurePatrolDataLoaded();
        if (!loaded) {
            return; // Error already set by ensurePatrolDataLoaded
        }

        const patrolData = activePatrolDataRef.current;
        if (!patrolData?.patrolInfo?.comment_list_id) {
            setLocalError('No active patrol. Start a patrol first.');
            return;
        }

        socketStartRecording(
            patrolData.patrolInfo.comment_list_id,
            patrolData.patrolInfo.schedule_assignment_id
        );
    }, [socketStartRecording, ensurePatrolDataLoaded, activePatrolDataRef]);

    // Computed value: whether there's a device connected to the current patrol
    const hasDeviceForPatrol = connectedDevice !== null;

    // Combine errors
    const error = socketError || localError;

    const clearError = useCallback((): void => {
        setLocalError(null);
    }, []);

    const value: CancamContextType = {
        // Connection
        isConnected,

        // Patrol-based device
        connectedDevice,
        hasDeviceForPatrol,

        // Pairing code generation
        patrolPairingCode,
        patrolCodeExpiry,
        generatePatrolCode,

        // Device replacement
        pendingReplacement,
        confirmDeviceReplacement,

        // Device state & controls
        deviceState,
        uploadProgress,
        startRecording,
        stopRecording: socketStopRecording,
        forceUpload: socketForceUpload,

        // Drawer
        isDrawerOpen,
        openDrawer: () => setIsDrawerOpen(true),
        closeDrawer: () => setIsDrawerOpen(false),

        // Error handling
        error,
        clearError,
    };

    return (
        <CancamContext.Provider value={value}>
            {children}
        </CancamContext.Provider>
    );
};

export const useCancam = (): CancamContextType => {
    const context = useContext(CancamContext);
    if (!context) {
        throw new Error('useCancam must be used within a CancamProvider');
    }
    return context;
};
